import React, { useRef, useState, useEffect, useCallback } from 'react';
import ModalCloseButton from './ui/ModalCloseButton';
import { trackEvent } from '../utils/telemetry';

export default function SocialStoryModal({ isOpen, onClose, theme = {}, eventData = {} }) {
  const canvasRef = useRef(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [copied, setCopied] = useState(false);

  const themeName = theme?.name || eventData?.theme || 'Party Aesthetic';
  const occasion = eventData?.occasion || 'Celebration';
  const palette = theme?.palette || [
    { hex: '#F43F5E', name: 'Rose' },
    { hex: '#FB923C', name: 'Amber' },
    { hex: '#FBBF24', name: 'Gold' },
    { hex: '#0F172A', name: 'Midnight' }
  ];

  const zones = [
    { name: 'Focal & Cake Table', icon: '🎂', desc: 'Centerpiece statement' },
    { name: 'Photo Wall & Backdrop', icon: '📸', desc: 'Instagram backdrop' },
    { name: 'Cocktail & Drink Station', icon: '🍸', desc: 'Signature sips' },
    { name: 'Table Settings & Linens', icon: '✨', desc: 'Curated tablescape' }
  ];

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsGenerating(true);

    // Standard 9:16 Story dimensions (1080 x 1920)
    const W = 1080;
    const H = 1920;
    canvas.width = W;
    canvas.height = H;

    // Background: Luxurious deep dark gradient with subtle theme color tint
    const primaryHex = typeof palette[0] === 'object' ? palette[0].hex : (palette[0] || '#E11D48');
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#0C0A09'); // Stone 950
    bgGrad.addColorStop(0.5, '#1C1917'); // Stone 900
    bgGrad.addColorStop(1, '#09090B'); // Zinc 950
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Glowing atmospheric ambient orbs
    const radGrad = ctx.createRadialGradient(W * 0.8, H * 0.15, 50, W * 0.8, H * 0.15, 500);
    radGrad.addColorStop(0, `${primaryHex}33`); // 20% opacity
    radGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = radGrad;
    ctx.fillRect(0, 0, W, H);

    // Outer Border Frame
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 4;
    ctx.strokeRect(40, 40, W - 80, H - 80);

    // Brand Header Tag
    ctx.fillStyle = primaryHex;
    ctx.font = 'bold 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('✨ CRAFTMYPARTY • BLUEPRINT', W / 2, 130);

    // Occasion Eyebrow
    ctx.fillStyle = '#A1A1AA'; // Zinc 400
    ctx.font = '600 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(occasion.toUpperCase(), W / 2, 185);

    // Main Theme Title
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '900 64px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    // Multi-line wrap for long theme names
    const words = themeName.split(' ');
    let line = '';
    let y = 260;
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > W - 160 && n > 0) {
        ctx.fillText(line.trim(), W / 2, y);
        line = words[n] + ' ';
        y += 75;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line.trim(), W / 2, y);

    // Color Palette Swatches Row
    const paletteY = y + 70;
    const swatchCount = Math.min(palette.length, 5);
    const swatchSpacing = 160;
    const startX = (W - (swatchCount - 1) * swatchSpacing) / 2;

    palette.slice(0, 5).forEach((color, i) => {
      const hex = typeof color === 'object' ? color.hex : color;
      const name = typeof color === 'object' ? color.name : hex;
      const x = startX + i * swatchSpacing;

      // Swatch Circle Shadow
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 15;
      ctx.shadowOffsetY = 6;

      // Swatch Circle
      ctx.beginPath();
      ctx.arc(x, paletteY, 44, 0, Math.PI * 2);
      ctx.fillStyle = hex;
      ctx.fill();

      // Border
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Color Name Text
      ctx.fillStyle = '#D4D4D8';
      ctx.font = '500 20px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(name.slice(0, 12), x, paletteY + 70);
    });

    // 4-Zone Cards Grid
    const gridStartY = paletteY + 120;
    const cardW = 460;
    const cardH = 340;
    const gap = 40;
    const gridLeft = (W - (cardW * 2 + gap)) / 2;

    zones.forEach((zone, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const cx = gridLeft + col * (cardW + gap);
      const cy = gridStartY + row * (cardH + gap);

      // Card Container
      ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 2;
      
      // Rounded rect
      ctx.beginPath();
      ctx.roundRect(cx, cy, cardW, cardH, 24);
      ctx.fill();
      ctx.stroke();

      // Zone Index Badge
      ctx.fillStyle = `${primaryHex}33`;
      ctx.beginPath();
      ctx.roundRect(cx + 24, cy + 24, 70, 36, 12);
      ctx.fill();

      ctx.fillStyle = primaryHex;
      ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`ZONE ${idx + 1}`, cx + 59, cy + 48);

      // Zone Icon
      ctx.font = '64px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(zone.icon, cx + cardW / 2, cy + 150);

      // Zone Title
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(zone.name, cx + cardW / 2, cy + 225);

      // Zone Desc
      ctx.fillStyle = '#A1A1AA';
      ctx.font = '500 18px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(zone.desc, cx + cardW / 2, cy + 265);
    });

    // Footer Watermark & Call-to-Action
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('Planned with CraftMyParty.fun', W / 2, H - 110);

    ctx.fillStyle = '#71717A';
    ctx.font = '500 20px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('AI-Powered Spatial Blueprints & Sourcing', W / 2, H - 75);

    try {
      const url = canvas.toDataURL('image/png');
      setDownloadUrl(url);
    } catch (e) {
      // ignore
    }
    setIsGenerating(false);
  }, [themeName, occasion, palette, zones]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(renderCanvas, 150);
    }
  }, [isOpen, renderCanvas]);

  if (!isOpen) return null;

  const handleShare = async () => {
    trackEvent('social_story_share_clicked', { theme: themeName });
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (navigator.share && navigator.canShare) {
      try {
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], `${themeName.toLowerCase().replace(/\s+/g, '-')}-story.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `${themeName} - Party Moodboard`,
              text: `Check out our party blueprint for ${occasion}! Planned on CraftMyParty.fun`,
            });
          }
        });
      } catch (err) {
        // user cancelled or failed, fallback to download
      }
    } else if (downloadUrl) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${themeName.toLowerCase().replace(/\s+/g, '-')}-story.png`;
      a.click();
    }
  };

  const handleDownload = () => {
    trackEvent('social_story_download_clicked', { theme: themeName });
    if (!downloadUrl) return;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `${themeName.toLowerCase().replace(/\s+/g, '-')}-story.png`;
    a.click();
  };

  const handleCopy = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        // eslint-disable-next-line no-undef
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setCopied(true);
        trackEvent('social_story_copied');
        setTimeout(() => setCopied(false), 2500);
      });
    } catch (e) {
      // fallback
    }
  };

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
      <div 
        className="relative w-full max-w-lg max-h-[92vh] bg-stone-950 rounded-2xl shadow-2xl border border-stone-800 flex flex-col overflow-hidden text-white"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-pink-500/20 text-pink-400 text-lg">
              📸
            </span>
            <div>
              <h2 className="text-base font-black text-white leading-tight">
                Instagram & TikTok Story Card
              </h2>
              <p className="text-xs text-stone-400">
                Aesthetic 9:16 story card ready to post
              </p>
            </div>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>

        {/* Live Canvas Preview (scaled down to fit UI) */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center justify-center bg-stone-900/50">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-stone-800" style={{ width: '270px', height: '480px' }}>
            <canvas
              ref={canvasRef}
              className="w-full h-full object-contain"
            />
            {isGenerating && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-xs font-bold text-white">
                Styling story card...
              </div>
            )}
          </div>
          <p className="text-[11px] text-stone-400 mt-3 text-center">
            Standard 1080 × 1920 HD Story format with your 4-zone moodboard.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-stone-800 bg-stone-900 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:to-amber-600 text-white font-extrabold text-xs transition shadow-md active:scale-[0.98]"
            >
              <span>📲</span>
              <span>Share to Story</span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-stone-800 hover:bg-stone-700 text-white font-bold text-xs transition border border-stone-700 active:scale-[0.98]"
            >
              <span>💾</span>
              <span>Download Image</span>
            </button>
          </div>

          <div className="flex items-center justify-between text-xs pt-1 px-1">
            <button
              type="button"
              onClick={handleCopy}
              className="text-stone-400 hover:text-white font-semibold transition flex items-center gap-1"
            >
              <span>{copied ? '✓' : '📋'}</span>
              <span>{copied ? 'Copied to Clipboard!' : 'Copy to Clipboard'}</span>
            </button>
            <span className="text-[11px] text-stone-400 font-medium">100% Free · 0 Credits</span>
          </div>
        </div>
      </div>
    </div>
  );
}
